import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Download, Trash2, Loader2, Search, Calculator, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  getChargesRegulations, createChargesRegulation, updateChargesRegulation,
  deleteChargesRegulation, downloadChargesRegulationPdf, getTenants,
} from "@/lib/api";
import { toast } from "sonner";

// Catégories de charges prédéfinies
const CATEGORIES = [
  "Eau froide",
  "Eau chaude",
  "Électricité parties communes",
  "Chauffage collectif",
  "Entretien ascenseur",
  "Entretien / nettoyage parties communes",
  "Gardiennage",
  "Assurance immeuble",
  "Taxe foncière (quote-part)",
  "Taxe ordures ménagères",
  "Frais de gestion",
  "Divers travaux",
  "Autre",
];

const EMPTY_FORM = {
  tenant_id: "",
  periode: String(new Date().getFullYear() - 1),
  date_arrete: `${new Date().getFullYear() - 1}-12-31`,
  provisions_payees: "",
  lignes: [{ libelle: "", montant: "" }],
  note: "",
  status: "brouillon",
};

const STATUS_COLORS = {
  brouillon: "secondary",
  envoyé:    "default",
  réglé:     "outline",
};

const fmtEuros = (v) =>
  Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export default function ChargesRegulation() {
  const [regulations, setRegulations] = useState([]);
  const [tenants, setTenants]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("tous");
  const [formData, setFormData]       = useState(EMPTY_FORM);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [regRes, tenantsRes] = await Promise.all([
        getChargesRegulations(),
        getTenants(),
      ]);
      setRegulations(regRes.data || []);
      setTenants(tenantsRes.data || []);
    } catch (err) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Helpers form ────────────────────────────────────────────────────────

  const set = (key) => (e) =>
    setFormData((p) => ({ ...p, [key]: e.target ? e.target.value : e }));

  // Calculs en temps réel
  const totalReel = formData.lignes.reduce(
    (acc, l) => acc + (parseFloat(l.montant) || 0), 0
  );
  const provisions = parseFloat(formData.provisions_payees) || 0;
  const solde = totalReel - provisions;

  // ── Lignes de charges ───────────────────────────────────────────────────

  const addLigne = () =>
    setFormData((p) => ({ ...p, lignes: [...p.lignes, { libelle: "", montant: "" }] }));

  const removeLigne = (i) =>
    setFormData((p) => ({ ...p, lignes: p.lignes.filter((_, idx) => idx !== i) }));

  const updateLigne = (i, key, val) =>
    setFormData((p) => {
      const lignes = [...p.lignes];
      lignes[i] = { ...lignes[i], [key]: val };
      return { ...p, lignes };
    });

  const addCategorie = (cat) => {
    if (!cat) return;
    setFormData((p) => ({
      ...p,
      lignes: [...p.lignes.filter((l) => l.libelle || l.montant), { libelle: cat, montant: "" }],
    }));
  };

  // ── Soumission ──────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tenant_id) { toast.error("Sélectionnez un locataire"); return; }
    if (!formData.periode)  { toast.error("Indiquez la période"); return; }

    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        provisions_payees: parseFloat(formData.provisions_payees) || 0,
        lignes: formData.lignes
          .filter((l) => l.libelle && (parseFloat(l.montant) || 0) > 0)
          .map((l) => ({ libelle: l.libelle, montant: parseFloat(l.montant) })),
      };
      await createChargesRegulation(payload);
      toast.success("Régulation créée !");
      setFormData(EMPTY_FORM);
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur création");
    } finally {
      setFormLoading(false);
    }
  };

  const handleStatusChange = async (reg, newStatus) => {
    try {
      await updateChargesRegulation(reg.id, { status: newStatus });
      toast.success("Statut mis à jour");
      fetchAll();
    } catch {
      toast.error("Erreur mise à jour");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette régulation ?")) return;
    try {
      await deleteChargesRegulation(id);
      toast.success("Supprimé");
      fetchAll();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const handleDownload = async (reg) => {
    setDownloadingId(reg.id);
    try {
      const tenant = tenants.find((t) => t.id === (reg.tenant_id || reg.lease_id));
      const nom   = tenant?.name || "locataire";
      const res   = await downloadChargesRegulationPdf(reg.id);
      const url   = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a     = document.createElement("a");
      a.href      = url;
      a.download  = `Regulation_charges_${nom}_${reg.periode}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur téléchargement PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Filtrage ────────────────────────────────────────────────────────────

  const leaseLabel = (id) => {
    const l = leases.find((x) => x.id === id);
    return l ? `${l.locataire_nom} — ${l.local_adresse || ""}` : id;
  };

  const filtered = regulations.filter((r) => {
    const q = searchQuery.toLowerCase();
    const label = tenantLabel(r).toLowerCase();
    const matchSearch = !q || label.includes(q) || r.periode?.includes(q);
    const matchStatus = filterStatus === "tous" || r.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // ── Statistiques ────────────────────────────────────────────────────────

  const totalSoldePositif = regulations
    .filter((r) => r.solde > 0)
    .reduce((a, r) => a + r.solde, 0);
  const totalSoldeNegatif = regulations
    .filter((r) => r.solde < 0)
    .reduce((a, r) => a + Math.abs(r.solde), 0);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Régulation des charges</h1>
          <p className="text-sm text-gray-500 mt-1">Réconciliation annuelle des provisions et charges réelles</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setFormData(EMPTY_FORM)} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle régulation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Créer une régulation des charges</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 pt-2">

              {/* Bail + Période */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Locataire *</Label>
                  <Select value={formData.tenant_id} onValueChange={(v) => {
                    const t = tenants.find((x) => x.id === v);
                    const prov = t?.charges_provisionnees
                      ? (parseFloat(t.charges_provisionnees) * 12).toFixed(2)
                      : "";
                    setFormData((p) => ({ ...p, tenant_id: v, provisions_payees: prov }));
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un locataire..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.sort((a,b)=>a.name.localeCompare(b.name)).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <>{t.name}{t.structure && <span className="text-gray-400"> — {t.structure}</span>}</>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Période (année) *</Label>
                  <Input
                    value={formData.periode}
                    onChange={set("periode")}
                    placeholder="2025"
                  />
                </div>
              </div>

              {/* Date arrêté + Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Date d'arrêté</Label>
                  <Input type="date" value={formData.date_arrete} onChange={set("date_arrete")} />
                </div>
                <div className="space-y-1">
                  <Label>Statut</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData((p) => ({ ...p, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="brouillon">Brouillon</SelectItem>
                      <SelectItem value="envoyé">Envoyé</SelectItem>
                      <SelectItem value="réglé">Réglé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Provisions */}
              <div className="space-y-1">
                <Label>Provisions sur charges versées sur l'année (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.provisions_payees}
                  onChange={set("provisions_payees")}
                  placeholder="1 200,00"
                />
                {formData.tenant_id && (() => {
                  const l = tenants.find((x) => x.id === formData.tenant_id);
                  return l?.provision_charges ? (
                    <p className="text-xs text-gray-400">
                      Provision mensuelle du bail : {fmtEuros(l.provision_charges)} × 12 = {fmtEuros(l.provision_charges * 12)}
                    </p>
                  ) : null;
                })()}
              </div>

              {/* Lignes de charges */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Charges réelles</Label>
                  <div className="flex gap-2">
                    <Select onValueChange={addCategorie}>
                      <SelectTrigger className="w-52 h-8 text-xs">
                        <SelectValue placeholder="+ Ajouter catégorie..." />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="outline" onClick={addLigne}>
                      <Plus className="w-3 h-3 mr-1" /> Ligne vide
                    </Button>
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-gray-600 w-2/3">Libellé</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-600 w-1/4">Montant (€)</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.lignes.map((ligne, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">
                            <Input
                              value={ligne.libelle}
                              onChange={(e) => updateLigne(i, "libelle", e.target.value)}
                              placeholder="Libellé de la charge"
                              className="border-0 shadow-none focus-visible:ring-0 h-8"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={ligne.montant}
                              onChange={(e) => updateLigne(i, "montant", e.target.value)}
                              placeholder="0,00"
                              className="border-0 shadow-none focus-visible:ring-0 h-8 text-right"
                            />
                          </td>
                          <td className="px-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeLigne(i)}
                              className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                            >
                              ×
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Récapitulatif en temps réel */}
              <div className={`rounded-lg p-4 border-2 ${
                solde > 0 ? "border-red-200 bg-red-50" :
                solde < 0 ? "border-green-200 bg-green-50" :
                "border-gray-200 bg-gray-50"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-4 h-4 text-gray-500" />
                  <span className="font-semibold text-sm text-gray-700">Récapitulatif</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500 text-xs">Total charges réelles</div>
                    <div className="font-bold text-gray-900">{fmtEuros(totalReel)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">Provisions versées</div>
                    <div className="font-bold text-gray-900">{fmtEuros(provisions)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs">Solde</div>
                    <div className={`font-bold text-lg ${
                      solde > 0 ? "text-red-600" : solde < 0 ? "text-green-600" : "text-gray-600"
                    }`}>
                      {solde > 0 ? "+" : ""}{fmtEuros(solde)}
                    </div>
                    <div className="text-xs text-gray-400">
                      {solde > 0 ? "→ Appel de fonds" : solde < 0 ? "→ Avoir locataire" : "→ Équilibré"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="space-y-1">
                <Label>Note (optionnel)</Label>
                <Textarea
                  value={formData.note}
                  onChange={set("note")}
                  placeholder="Observations, précisions..."
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Créer la régulation
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Cartes stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Calculator className="w-5 h-5 text-blue-600" /></div>
              <div>
                <div className="text-sm text-gray-500">Total régulations</div>
                <div className="text-2xl font-bold">{regulations.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg"><TrendingUp className="w-5 h-5 text-red-600" /></div>
              <div>
                <div className="text-sm text-gray-500">Appels de fonds</div>
                <div className="text-2xl font-bold text-red-600">{fmtEuros(totalSoldePositif)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><TrendingDown className="w-5 h-5 text-green-600" /></div>
              <div>
                <div className="text-sm text-gray-500">Avoirs locataires</div>
                <div className="text-2xl font-bold text-green-600">{fmtEuros(totalSoldeNegatif)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Rechercher un locataire, une période..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les statuts</SelectItem>
                <SelectItem value="brouillon">Brouillon</SelectItem>
                <SelectItem value="envoyé">Envoyé</SelectItem>
                <SelectItem value="réglé">Réglé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Régulations ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Calculator className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Aucune régulation</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Locataire / Bail</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead className="text-right">Charges réelles</TableHead>
                  <TableHead className="text-right">Provisions</TableHead>
                  <TableHead className="text-right">Solde</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((reg) => {
                  const tenant = tenants.find((t) => t.id === (reg.tenant_id || reg.lease_id));
                  return (
                    <TableRow key={reg.id}>
                      <TableCell>
                        <div className="font-medium">{tenant?.name || "—"}</div>
                        <div className="text-xs text-gray-400">{tenant?.structure || reg.tenant_id || reg.lease_id}</div>
                      </TableCell>
                      <TableCell className="font-medium">{reg.periode}</TableCell>
                      <TableCell className="text-right">{fmtEuros(reg.total_reel)}</TableCell>
                      <TableCell className="text-right text-gray-500">{fmtEuros(reg.provisions_payees)}</TableCell>
                      <TableCell className="text-right">
                        <span className={`font-bold ${
                          reg.solde > 0 ? "text-red-600" :
                          reg.solde < 0 ? "text-green-600" :
                          "text-gray-500"
                        }`}>
                          {reg.solde > 0
                            ? <span className="flex items-center justify-end gap-1"><TrendingUp className="w-3 h-3" />+{fmtEuros(reg.solde)}</span>
                            : reg.solde < 0
                            ? <span className="flex items-center justify-end gap-1"><TrendingDown className="w-3 h-3" />{fmtEuros(reg.solde)}</span>
                            : <span className="flex items-center justify-end gap-1"><Minus className="w-3 h-3" />Équilibré</span>
                          }
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={reg.status}
                          onValueChange={(v) => handleStatusChange(reg, v)}
                        >
                          <SelectTrigger className="w-32 h-7 text-xs">
                            <SelectValue>
                              <Badge variant={STATUS_COLORS[reg.status] || "secondary"} className="text-xs">
                                {reg.status}
                              </Badge>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="brouillon">Brouillon</SelectItem>
                            <SelectItem value="envoyé">Envoyé</SelectItem>
                            <SelectItem value="réglé">Réglé</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(reg)}
                            disabled={downloadingId === reg.id}
                            title="Télécharger PDF"
                          >
                            {downloadingId === reg.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Download className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(reg.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
